import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

const ImportModal = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await axios.get('/api/jobs?limit=1000');
        setJobs(response.data.jobs);
      } catch (err) {}
    };
    fetchJobs();
  }, []);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setError('');
    setPreview(null);
    setImportResult(null);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get('/api/import/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'applicant_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Error downloading template: ' + (err.response?.data?.message || err.message));
    }
  };

  // Step 1: Preview
  const handlePreview = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please select a file'); return; }

    setLoading(true);
    setError('');
    setPreview(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (selectedJobId) formData.append('jobId', selectedJobId);

      const response = await axios.post('/api/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreview(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error previewing file');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Confirm
  const handleConfirm = async () => {
    const hasReady = preview && preview.readyRows && preview.readyRows.length > 0;
    const hasMerge = preview && preview.mergeRows && preview.mergeRows.length > 0;
    if (!hasReady && !hasMerge) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/import/confirm', {
        rows: preview.readyRows || [],
        mergeRows: preview.mergeRows || [],
        jobId: selectedJobId ? parseInt(selectedJobId) : null,
        fileName: preview.fileName,
        previewStats: {
          totalRows: preview.totalRows,
          duplicatesToMerge: preview.duplicatesToMerge || 0,
          skipped: preview.skipped,
          errors: preview.errors,
          mergeDetails: preview.mergeDetails,
          skipDetails: preview.skipDetails,
          errorDetails: preview.errorDetails
        }
      });
      setImportResult(response.data);
      setPreview(null);
      setTimeout(() => { onSuccess(); }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error importing');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setImportResult(null);
    setError('');
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Import Applicants</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}

        {/* Final import result */}
        {importResult && (
          <div className="mb-4 rounded-md bg-green-50 p-4">
            <div className="text-sm text-green-800">
              <p className="font-medium">{importResult.message}</p>
            </div>
          </div>
        )}

        {/* Preview summary */}
        {preview && !importResult && (
          <div className="mb-4 space-y-3">
            <div className="rounded-md bg-blue-50 p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">Preview Summary</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <p>Total rows in file: <span className="font-medium">{preview.totalRows}</span></p>
                <p className="text-green-700">Ready to import (new): <span className="font-bold">{preview.readyToImport}</span></p>
                {preview.duplicatesToMerge > 0 && (
                  <p className="text-yellow-700">Duplicates (will merge): <span className="font-bold">{preview.duplicatesToMerge}</span></p>
                )}
                {preview.skipped > 0 && (
                  <p className="text-gray-600">No phone number (skipped): <span className="font-bold">{preview.skipped}</span></p>
                )}
                {preview.errors > 0 && (
                  <p className="text-red-700">Errors: <span className="font-bold">{preview.errors}</span></p>
                )}
              </div>
            </div>

            {preview.errorDetails && preview.errorDetails.length > 0 && (
              <div className="rounded-md bg-red-50 p-4">
                <h4 className="text-sm font-semibold text-red-900 mb-1">Error Details</h4>
                <ul className="text-sm text-red-800 list-disc list-inside">
                  {preview.errorDetails.map((d, idx) => (
                    <li key={idx}>Row {d.row}: {d.error}</li>
                  ))}
                  {preview.errors > 10 && <li>... and {preview.errors - 10} more</li>}
                </ul>
              </div>
            )}

            {preview.skipDetails && preview.skipDetails.length > 0 && (
              <div className="rounded-md bg-gray-50 p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-1">Skipped Details</h4>
                <ul className="text-sm text-gray-700 list-disc list-inside">
                  {preview.skipDetails.map((d, idx) => (
                    <li key={idx}>Row {d.row}: {d.name || '(unnamed)'} — {d.reason}</li>
                  ))}
                  {preview.skipped > 10 && <li>... and {preview.skipped - 10} more</li>}
                </ul>
              </div>
            )}

            {preview.mergeDetails && preview.mergeDetails.length > 0 && (
              <div className="rounded-md bg-yellow-50 p-4">
                <h4 className="text-sm font-semibold text-yellow-900 mb-1">Merge Details (duplicates)</h4>
                <ul className="text-sm text-yellow-800 list-disc list-inside">
                  {preview.mergeDetails.map((d, idx) => (
                    <li key={idx}>Row {d.row}: {d.name} ({d.phone}) — will merge into "{d.existingName}"</li>
                  ))}
                  {preview.duplicatesToMerge > 10 && <li>... and {preview.duplicatesToMerge - 10} more</li>}
                </ul>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={handleReset}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={loading || (preview.readyToImport === 0 && (!preview.duplicatesToMerge || preview.duplicatesToMerge === 0))}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Importing...' : `Confirm Import (${preview.readyToImport + (preview.duplicatesToMerge || 0)})`}
              </button>
            </div>
          </div>
        )}

        {/* Upload form — only when no preview and no result */}
        {!preview && !importResult && (
          <form onSubmit={handlePreview} className="space-y-4">
            {jobs.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link to Job (optional)
                </label>
                <select
                  value={selectedJobId}
                  onChange={(e) => setSelectedJobId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">— No job selected —</option>
                  {jobs.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.position} — {job.title}{job.city ? ` (${job.city})` : ''}
                    </option>
                  ))}
                </select>
                {selectedJobId && (
                  <p className="mt-1 text-xs text-gray-500">
                    Position will be set from the selected job for all imported applicants.
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select CSV or Excel file
              </label>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Required columns: name, phone (optional: email, position, status, notes)
              </p>
            </div>

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={handleDownloadTemplate}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                Download Template
              </button>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !file}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Analyzing...' : 'Preview'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ImportModal;


